import React, { useEffect, useState, useRef, FC, useMemo } from 'react';
import {
  View,
  FlatList,
  Animated,
  Platform,
  TouchableOpacity,
} from 'react-native';
import { ICarousel } from '@common/types/element';
import CarouselItem from './CarouselItem';
import querystring from 'query-string';
import Icon from '../AppBar/Icon';
import createStyles from './style';

const INDICATORS_POSITION = {
  OUTSIDE: 1,
  INSIDE: 2,
};

const getCarouselItemList = (attrs: ICarousel) => {
  const { dataBinding } = attrs;

  if (Platform.OS !== 'web') {
    return dataBinding ?? [];
  } else {
    const search = querystring.parse(window?.location?.search);
    const target = search?.target;

    if (target) {
      return dataBinding ?? [];
    } else {
      return (
        dataBinding ??
        Array.from(Array(3), () => (dataBinding ? dataBinding[0] : ''))
      );
    }
  }
};

const isCanvas = () => {
  const isWeb = Platform.OS === 'web';

  if (!isWeb) return false;

  const search = querystring.parse(window?.location?.search);
  const target = search?.target;

  return target ? false : true;
};

const Carousel: FC<ICarousel> = (attrs) => {
  const { attributes, width, height, onPress } = attrs;
  const { autoScroll, slideIndicators, arrows } = attributes;
  const [sliderState, setsliderState] = useState({
    currentScrollValue: 0,
    scrollOffsetValue: 0,
  });
  const flatlist = useRef<FlatList<any>>(null);
  const isMounted = useRef<boolean>(false);
  const interval = useRef<any>(null);
  const items = getCarouselItemList(attrs);
  const scrollX = new Animated.Value(0);
  const data_len = items.length;
  const styles = createStyles({ ...attributes, width });
  let position = Animated.divide(scrollX, width);

  const scrollSlider = () => {
    if (!!flatlist) {
      flatlist?.current?.scrollToOffset({
        animated: true,
        offset: sliderState?.scrollOffsetValue,
      });
    }
  };

  const infiniteAutoScroll = () => {
    if (interval.current !== null) {
      clearTimeout(interval.current);
    }

    interval.current = setTimeout(() => {
      if (sliderState.currentScrollValue + 1 < data_len) {
        setsliderState((prev) => ({
          currentScrollValue: prev.currentScrollValue + 1,
          scrollOffsetValue: prev.scrollOffsetValue + width,
        }));
      } else {
        setsliderState({
          currentScrollValue: 0,
          scrollOffsetValue: 0,
        });
      }
    }, autoScroll.autoScrollSpeed * 1000);

    return interval.current;
  };

  const slide = (direction: string) => {
    if (direction === 'left') {
      if (sliderState?.currentScrollValue - 1 < 0) return;
      setsliderState({
        currentScrollValue: sliderState.currentScrollValue - 1,
        scrollOffsetValue: sliderState.scrollOffsetValue - width,
      });
    }

    if (direction === 'right') {
      if (sliderState?.currentScrollValue + 1 === data_len) return;
      setsliderState({
        currentScrollValue: sliderState.currentScrollValue + 1,
        scrollOffsetValue: sliderState.scrollOffsetValue + width,
      });
    }
  };

  useEffect(() => {
    if (isCanvas()) return;
    if (autoScroll?.enabled) infiniteAutoScroll();
    return () => clearTimeout(interval.current);
  }, [items]);

  useEffect(() => {
    if (isCanvas()) return;
    if (isMounted.current) {
      if (autoScroll?.enabled) {
        infiniteAutoScroll();
      }
      scrollSlider();
    } else {
      isMounted.current = true;
    }
    return () => clearTimeout(interval.current);
  }, [sliderState]);

  const getDataWidth = (): number => items?.length ?? 10;
  const renderer = ({ item, index }: any) => (
    <CarouselItem
      item={item}
      key={index}
      index={index}
      onPress={onPress}
      attributes={attributes}
      height={height}
      width={width}
    />
  );
  const momoizedValue = useMemo(() => renderer, items);

  return (
    <View style={styles.wrapper}>
      <View style={{ position: 'relative', flex: 1 }}>
        {isCanvas() ? (
          <View style={{ width, height, backgroundColor: '#eee' }}></View>
        ) : (
          <FlatList
            data={items}
            renderItem={momoizedValue}
            ref={flatlist}
            horizontal
            showsHorizontalScrollIndicator={false}
            snapToAlignment="start"
            scrollEventThrottle={16}
            decelerationRate={'normal'}
            pagingEnabled
            scrollEnabled
            style={{ width, height }}
            onScroll={Animated.event([
              { nativeEvent: { contentOffset: { x: scrollX } } },
            ])}
            initialNumToRender={getDataWidth()}
          />
        )}

        {arrows?.enabled && (
          <>
            <View style={[styles.arrowWrapper, { left: 0 }]}>
              <TouchableOpacity
                onPress={() => slide('left')}
                style={[
                  styles.arrow,
                  {
                    backgroundColor:
                      sliderState.currentScrollValue - 1 < 0
                        ? arrows.iconDisabledColor
                        : arrows.iconColor,
                  },
                ]}
              >
                <Icon name={arrows.leftIcon ?? 'chevron-left'} color={'#FFF'} />
              </TouchableOpacity>
            </View>
            <View style={[styles.arrowWrapper, { right: 0 }]}>
              <TouchableOpacity
                onPress={() => slide('right')}
                style={[
                  styles.arrow,
                  {
                    backgroundColor:
                      sliderState.currentScrollValue + 1 === data_len
                        ? arrows.iconDisabledColor
                        : arrows.iconColor,
                  },
                ]}
              >
                <Icon
                  name={arrows.rightIcon ?? 'chevron-left'}
                  color={'#FFF'}
                />
              </TouchableOpacity>
            </View>
          </>
        )}
      </View>
      {slideIndicators?.enabled && (
        <View
          style={[
            styles.dots,
            slideIndicators.position === INDICATORS_POSITION.INSIDE
              ? styles.insideDots
              : {},
          ]}
        >
          {(items ?? []).map((_: any, i: any) => {
            let opacity = position.interpolate({
              inputRange: [i - 1, i, i + 1],
              outputRange: [0.6, 1, 0.6],
              extrapolate: 'clamp',
            });
            return <Animated.View key={i} style={[styles.dot, { opacity }]} />;
          })}
        </View>
      )}
    </View>
  );
};

export default Carousel;
